/**
 * 問い合わせ管理用カスタムフック
 */

import type {
  ContactListQuery,
  ContactListResult,
  ContactRecord,
  ContactStats,
  ContactStatus,
} from "@repo/types";
import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/api-client";

interface UseContactsResult {
  contacts: ContactRecord[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  } | null;
  isLoading: boolean;
  error: string | null;
  query: ContactListQuery;
  setQuery: (query: Partial<ContactListQuery>) => void;
  refresh: () => Promise<void>;
}

interface UseContactResult {
  contact: ContactRecord | null;
  isLoading: boolean;
  error: string | null;
  updateStatus: (status: ContactStatus, note?: string) => Promise<boolean>;
  addNote: (content: string) => Promise<boolean>;
  sendReply: (subject: string, message: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

interface UseContactStatsResult {
  stats: ContactStats | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

// ============================================
// 問い合わせ一覧フック
// ============================================

export function useContacts(): UseContactsResult {
  const [contacts, setContacts] = useState<ContactRecord[]>([]);
  const [pagination, setPagination] = useState<ContactListResult["pagination"] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQueryState] = useState<ContactListQuery>({
    page: 1,
    limit: 20,
    sortBy: "createdAt",
    sortOrder: "desc",
  });

  const fetchContacts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.api.admin.contacts.$get({
        query: {
          page: query.page.toString(),
          limit: query.limit.toString(),
          sortBy: query.sortBy,
          sortOrder: query.sortOrder,
          ...(query.status && { status: query.status }),
          ...(query.category && { category: query.category }),
          ...(query.startDate && { startDate: query.startDate }),
          ...(query.endDate && { endDate: query.endDate }),
        },
      });

      if (response.ok) {
        const data = (await response.json()) as ContactListResult;
        setContacts(data.contacts);
        setPagination(data.pagination);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError((errorData as { error?: string }).error || "問い合わせ一覧の取得に失敗しました");
      }
    } catch (_err) {
      setError("通信エラーが発生しました");
    } finally {
      setIsLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const setQuery = useCallback((newQuery: Partial<ContactListQuery>) => {
    setQueryState((prev) => ({
      ...prev,
      ...newQuery,
      // フィルタが変更されたらページを1に戻す
      page: newQuery.page ?? (Object.keys(newQuery).some((k) => k !== "page") ? 1 : prev.page),
    }));
  }, []);

  return {
    contacts,
    pagination,
    isLoading,
    error,
    query,
    setQuery,
    refresh: fetchContacts,
  };
}

// ============================================
// 問い合わせ詳細フック
// ============================================

export function useContact(contactId: string | null): UseContactResult {
  const [contact, setContact] = useState<ContactRecord | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchContact = useCallback(async () => {
    if (!contactId) {
      setContact(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.api.admin.contacts[":id"].$get({
        param: { id: contactId },
      });

      if (response.ok) {
        const data = (await response.json()) as ContactRecord;
        setContact(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError((errorData as { error?: string }).error || "問い合わせの取得に失敗しました");
      }
    } catch (_err) {
      setError("通信エラーが発生しました");
    } finally {
      setIsLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    fetchContact();
  }, [fetchContact]);

  // ステータス更新
  const updateStatus = useCallback(
    async (status: ContactStatus, note?: string) => {
      if (!contactId) return false;

      try {
        const response = await apiClient.api.admin.contacts[":id"].status.$patch({
          param: { id: contactId },
          json: { status, note },
        });

        if (response.ok) {
          const data = (await response.json()) as { success: boolean; contact: ContactRecord };
          setContact(data.contact);
          return true;
        }
        return false;
      } catch {
        return false;
      }
    },
    [contactId],
  );

  // メモ追加
  const addNote = useCallback(
    async (content: string) => {
      if (!contactId) return false;

      try {
        const response = await apiClient.api.admin.contacts[":id"].notes.$post({
          param: { id: contactId },
          json: { content },
        });

        if (response.ok) {
          const data = (await response.json()) as { success: boolean; contact: ContactRecord };
          setContact(data.contact);
          return true;
        }
        return false;
      } catch {
        return false;
      }
    },
    [contactId],
  );

  // 返信送信
  const sendReply = useCallback(
    async (subject: string, message: string) => {
      if (!contactId) return false;

      try {
        const response = await apiClient.api.admin.contacts[":id"].reply.$post({
          param: { id: contactId },
          json: { subject, message },
        });

        if (response.ok) {
          const data = (await response.json()) as { success: boolean; contact: ContactRecord };
          setContact(data.contact);
          return true;
        }
        return false;
      } catch {
        return false;
      }
    },
    [contactId],
  );

  return {
    contact,
    isLoading,
    error,
    updateStatus,
    addNote,
    sendReply,
    refresh: fetchContact,
  };
}

// ============================================
// 統計情報フック
// ============================================

export function useContactStats(): UseContactStatsResult {
  const [stats, setStats] = useState<ContactStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.api.admin.contacts.stats.$get();

      if (response.ok) {
        const data = (await response.json()) as ContactStats;
        setStats(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError((errorData as { error?: string }).error || "統計情報の取得に失敗しました");
      }
    } catch (_err) {
      setError("通信エラーが発生しました");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    isLoading,
    error,
    refresh: fetchStats,
  };
}
